import React, { createContext, useContext, useEffect, useState } from "react";
import { User, onAuthStateChanged, signInWithCustomToken, signInWithPopup, signOut as firebaseSignOut } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, googleProvider, db } from "../firebase";
import { clearGalleryImageCache } from "../utils/galleryImageCache";

interface AuthContextType {
  currentUser: User | null;
  isAdmin: boolean;
  isGuest: boolean;
  guestName: string | null;
  loading: boolean;
  signInWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  signInAsGuest: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [guestName, setGuestName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let guestExpiryTimer: ReturnType<typeof setTimeout> | undefined;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("🔐 Auth state changed:", user ? `User: ${user.email}` : "No user");
      clearTimeout(guestExpiryTimer);
      // Signed out (including a guest session expiring): drop cached gallery photos.
      if (!user) clearGalleryImageCache();
      setIsGuest(false);
      setGuestName(null);

      // Photo gallery guests sign in with a custom token that expires after 24 hours.
      // They never get a users/{uid} doc and are never admins.
      const claims = user ? (await user.getIdTokenResult()).claims : {};
      if (user && claims.guest === true) {
        const expiresAt = Number(claims.galleryExpiresAt) || 0;
        if (expiresAt <= Date.now()) {
          await firebaseSignOut(auth);
          return;
        }
        guestExpiryTimer = setTimeout(() => firebaseSignOut(auth), expiresAt - Date.now());
        setCurrentUser(user);
        setIsAdmin(false);
        setIsGuest(true);
        setGuestName(typeof claims.guestName === "string" ? claims.guestName : null);
        setLoading(false);
        return;
      }

      setCurrentUser(user);

      if (user) {
        try {
          // Check Firestore for admin status
          const userDocRef = doc(db, "users", user.uid);
          console.log("📄 Checking Firestore for user document:", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            console.log("✅ User document exists:", userDoc.data());
            setIsAdmin(userDoc.data()?.isAdmin === true);
          } else {
            console.log("❌ User document does NOT exist. Creating new document...");
            // Create user document on first login
            const userData = {
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL,
              isAdmin: false,
              createdAt: new Date().toISOString()
            };
            console.log("📝 Writing user document:", userData);
            await setDoc(userDocRef, userData);
            console.log("✅ User document created successfully!");
            setIsAdmin(false);
          }
        } catch (error) {
          console.error("❌ Error in onAuthStateChanged:", error);
        }
      } else {
        setIsAdmin(false);
      }

      setLoading(false);
    });

    return () => {
      clearTimeout(guestExpiryTimer);
      unsubscribe();
    };
  }, []);

  const signInWithGoogle = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log("🔑 Starting Google Sign-In...");
      const result = await signInWithPopup(auth, googleProvider);
      console.log("✅ Google Sign-In successful:", result.user.email);

      // Check if user is admin
      const userDocRef = doc(db, "users", result.user.uid);
      console.log("📄 Checking for user document in signInWithGoogle:", result.user.uid);
      const userDoc = await getDoc(userDocRef);

      let isUserAdmin = false;

      if (userDoc.exists()) {
        console.log("✅ User document exists in signInWithGoogle:", userDoc.data());
        isUserAdmin = userDoc.data()?.isAdmin === true;
      } else {
        console.log("❌ User document does NOT exist in signInWithGoogle. Creating...");
        // Create user document for new users
        const userData = {
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoURL,
          isAdmin: false,
          createdAt: new Date().toISOString()
        };
        console.log("📝 Writing user document in signInWithGoogle:", userData);
        await setDoc(userDocRef, userData);
        console.log("✅ User document created successfully in signInWithGoogle!");
      }

      console.log("🔐 isUserAdmin:", isUserAdmin);

      if (!isUserAdmin) {
        console.log("⛔ User is not admin. Signing out...");
        await firebaseSignOut(auth);
        return {
          success: false,
          error: "Not authorized. Contact administrator for access."
        };
      }

      console.log("✅ Admin user authenticated successfully!");
      return { success: true };
    } catch (error: any) {
      console.error("❌ Sign in error:", error);
      return {
        success: false,
        error: error.message || "Failed to sign in with Google"
      };
    }
  };

  const signInAsGuest = async (token: string) => {
    await signInWithCustomToken(auth, token);
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const value: AuthContextType = {
    currentUser,
    isAdmin,
    isGuest,
    guestName,
    loading,
    signInWithGoogle,
    signInAsGuest,
    signOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
